import { useState, useEffect } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { bruktbilfinansieringAPI } from '../../services/api'
import type { DealerDetailsResponse } from '../../types/bruktbilfinansiering'
import DealerSoldRowsTabs from './DealerSoldRowsTabs'
import { useVehicleFilter } from '../../contexts/VehicleFilterContext'

const COLORS = ['#1e3a8a', '#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#e879f9']
const fmtNum = (n: number) => new Intl.NumberFormat('nb-NO').format(n)
const fmtNok = (n: number) => new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(n)
const fmtPct = (n: number) => `${(n * 100).toFixed(1)} %`
const fmtDate = (iso: string) => iso.split('-').reverse().join('.')
const LAG_DAYS = 15
const subtractDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }

/** Which end_date the "Uten finansieringsdata" tab should ask for.
 *
 *  The page's end_date is usually the module-wide default of today - 15, set
 *  because loan data is not settled before then. Forwarding it would reimpose
 *  exactly the cutoff this tab exists to escape, so anything at or after that
 *  boundary is treated as "the default" and dropped — the backend then fills in
 *  MAX(hendelse_tidspunkt).
 *
 *  An end_date genuinely earlier than the boundary is a deliberate historical
 *  window (the user picked a past month) and is passed through untouched.
 */
function varebilLabelKey(varebil: boolean | undefined) {
  if (varebil === true) return 'shared.vehicle.varebil' as const
  if (varebil === false) return 'shared.vehicle.personbil' as const
  return 'shared.vehicle.alle' as const
}

function resolveSoldRowsEndDate(endDate: string | undefined): string | undefined {
  if (!endDate) return undefined
  return endDate < subtractDays(LAG_DAYS) ? endDate : undefined
}

export default function DealerDetailsPage() {
  const { t } = useTranslation('bbf')
  const { bankOrgNr = '', dealerOrgNr = '' } = useParams<{ bankOrgNr: string; dealerOrgNr: string }>()
  const [searchParams] = useSearchParams()
  const { getVarebilParam } = useVehicleFilter()
  const [data, setData] = useState<DealerDetailsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const startDate = searchParams.get('start_date') ?? `${new Date().getFullYear()}-01-01`
  const endDate = searchParams.get('end_date') ?? undefined
  const dealerPostnummer = searchParams.get('dealer_postnummer') ?? undefined
  const buyerPostnummer = searchParams.get('buyer_postnummer') ?? undefined
  const markedsomradeName = searchParams.get('markedsomrade_name') ?? null
  const varebil = getVarebilParam()
  const classesParam = searchParams.getAll('classes')
  const classes = classesParam.length > 0 ? classesParam : undefined

  useEffect(() => {
    setLoading(true)
    setError(null)
    bruktbilfinansieringAPI.getDealerDetails(bankOrgNr, dealerOrgNr, {
      start_date: startDate,
      end_date: endDate,
      varebil,
      dealer_postnummer: dealerPostnummer,
      buyer_postnummer: buyerPostnummer,
      classes,
    })
      .then(res => setData(res.data))
      .catch(e => setError(e instanceof Error ? e.message : t('errors.loadData')))
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOrgNr, dealerOrgNr, startDate, endDate, varebil, dealerPostnummer, buyerPostnummer, JSON.stringify(classes)])

  if (loading) return (
    <div className="p-6 flex items-center gap-3 text-gray-500">
      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      {t('details.loading')}
    </div>
  )
  if (error) return <div className="p-6 text-red-600">{t('shared.errorLabel')} {error}</div>
  if (!data) return null

  const { kpis, bank_breakdown, brand_mix } = data

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <Link
          to={`/bruktbil-finansiering/${bankOrgNr}?tab=forhandlere`}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> {t('details.back')}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{kpis.dealer_name}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">{kpis.dealer_org_nr}</p>
      </div>

      {/* Active filter info box */}
      {(() => {
        const classLabels = classes?.length
          ? [...new Set(classes.map(c => {
              if (c.endsWith('_TO_PERSON')) return 'buyerTypes.PERSON' as const
              if (c.endsWith('_TO_FIRMA')) return 'buyerTypes.FIRMA' as const
              if (c.endsWith('_TO_BILUTLEIE')) return 'buyerTypes.BILUTLEIE' as const
              return null
            }).filter(k => k !== null))].map(k => t(k))
          : null
        return (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
            <p className="mb-1">
              {t('details.intro', { dealer: kpis.dealer_name })}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              {t('details.period', { from: fmtDate(startDate), to: endDate ? fmtDate(endDate) : fmtDate(subtractDays(LAG_DAYS)) })}
              {` · ${t(varebilLabelKey(varebil))}`}
              {markedsomradeName && (dealerPostnummer || buyerPostnummer) && ` · ${t('shared.markedsomrade', { name: markedsomradeName })}`}
              {classLabels && classLabels.length < 3 && ` · ${t('details.buyerType', { types: classLabels.join(', ') })}`}
            </p>
          </div>
        )
      })()}

      {/* KPI cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label={t('dealers.cols.totalCars')} value={fmtNum(kpis.total_all_sales)} />
        <KpiCard label={t('dealers.cols.carsInScope')} value={fmtNum(kpis.total_sales)} />
        <KpiCard label={t('details.kpi.loanShare')} value={kpis.total_sales > 0 ? fmtPct(kpis.total_loans_all_banks / kpis.total_sales) : '—'} />
        <KpiCard label={t('dealers.cols.bankLoans')} value={fmtNum(kpis.bank_financed_count)} />
        <KpiCard label={t('dealers.cols.totalLoans')} value={fmtNum(kpis.total_loans_all_banks)} />
        <KpiCard label={t('details.kpi.marketShare')} value={kpis.total_loans_all_banks > 0 ? fmtPct(kpis.bank_market_share) : '—'} highlight />
      </div>
      {/* KPI cards - Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label={t('details.kpi.volumeBank')} value={fmtNok(kpis.bank_total_nok)} />
        <KpiCard label={t('details.kpi.volumeBanks')} value={fmtNok(kpis.total_volume_all_banks)} />
        <KpiCard label={t('details.kpi.avgLoan')} value={fmtNok(kpis.avg_loan_nok)} />
        <KpiCard label={t('details.kpi.avgBuyerAge')} value={kpis.buyer_avg_age != null ? t('shared.years', { value: kpis.buyer_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) : '—'} />
        <KpiCard label={t('details.kpi.avgBorrowerAge')} value={kpis.borrower_avg_age != null ? t('shared.years', { value: kpis.borrower_avg_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) : '—'} />
      </div>

      {/* Bank breakdown */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t('details.financiers')}</h2>
        {bank_breakdown.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={bank_breakdown}
                  dataKey="loan_count"
                  nameKey="bank_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ bank_name, share }: { bank_name: string; share: number }) => `${bank_name.split(' ')[0]} ${fmtPct(share)}`}
                  labelLine={false}
                >
                  {bank_breakdown.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => fmtNum(v)} />
              </PieChart>
            </ResponsiveContainer>
            <table className="text-sm w-full self-start">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">{t('shared.bank')}</th>
                  <th className="px-3 py-2 text-right">{t('shared.count')}</th>
                  <th className="px-3 py-2 text-right">{t('details.amount')}</th>
                  <th className="px-3 py-2 text-right">{t('shared.share')}</th>
                </tr>
              </thead>
              <tbody>
                {bank_breakdown.map((b, i) => (
                  <tr key={b.bank_org_nr} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-3 py-1.5 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      {b.bank_name}
                    </td>
                    <td className="px-3 py-1.5 text-right">{fmtNum(b.loan_count)}</td>
                    <td className="px-3 py-1.5 text-right">{fmtNok(b.total_belop)}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{fmtPct(b.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">{t('details.noFinancing')}</p>
        )}
      </section>

      {/* Brand mix */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{t('details.brandMix')}</h2>
        {brand_mix.length > 0 ? (
          <div className="overflow-x-auto" style={{ maxHeight: brand_mix.length > 10 ? '480px' : undefined, overflowY: brand_mix.length > 10 ? 'auto' : undefined }}>
            <table className="text-sm w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left">{t('shared.brand')}</th>
                  <th className="px-3 py-2 text-right">{t('details.sold')}</th>
                  <th className="px-3 py-2 text-right">{t('details.financed')}</th>
                  <th className="px-3 py-2 text-right">{t('details.rate')}</th>
                  <th className="px-3 py-2 text-right">{t('details.avgAge')}</th>
                  <th className="px-3 py-2 text-right">{t('details.medianAge')}</th>
                  <th className="px-3 py-2 text-left">{t('details.topBanks')}</th>
                  <th className="px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {brand_mix.map(b => (
                  <tr key={b.merke} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="px-3 py-1.5 font-medium">{b.merke}</td>
                    <td className="px-3 py-1.5 text-right">{fmtNum(b.total_sales)}</td>
                    <td className="px-3 py-1.5 text-right">{fmtNum(b.financed_count)}</td>
                    <td className="px-3 py-1.5 text-right">{fmtPct(b.finance_rate)}</td>
                    <td className="px-3 py-1.5 text-right">{b.avg_car_age != null ? t('shared.years', { value: b.avg_car_age.toFixed(1) }) : '—'}</td>
                    <td className="px-3 py-1.5 text-right">{b.median_car_age != null ? t('shared.years', { value: b.median_car_age.toFixed(1) }) : '—'}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(b.top_banks ?? []).slice(0, 5).map(tb => (
                          <span key={tb.bank_name} className="inline-block px-1.5 py-0.5 text-xs rounded-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200" title={t('details.topBankTitle', { bank: tb.bank_name, n: tb.loan_count, pct: fmtPct(tb.share) })}>
                            {tb.bank_short_code} {tb.loan_count}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(b.finance_rate * 100, 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">{t('details.noBrands')}</p>
        )}
      </section>

      {/* Solgte biler — two tabs (#407) */}
      <DealerSoldRowsTabs
        bankOrgNr={bankOrgNr}
        dealerOrgNr={dealerOrgNr}
        buyerRows={data.buyer_rows}
        totalSales={kpis.total_sales}
        financedSales={kpis.financed_sales}
        loanDataThrough={subtractDays(LAG_DAYS)}
        contextInfo={`${t('details.period', { from: fmtDate(startDate), to: endDate ? fmtDate(endDate) : '—' })} · ${t(varebilLabelKey(varebil))}${markedsomradeName && (dealerPostnummer || buyerPostnummer) ? ` · ${t('shared.markedsomrade', { name: markedsomradeName })}` : ''}`}
        soldRowsFilters={{
          startDate,
          endDate: resolveSoldRowsEndDate(endDate),
          varebil,
          classes,
          dealerPostnummer,
          buyerPostnummer,
        }}
      />
    </div>
  )
}

function KpiCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`border rounded-lg p-3 ${highlight ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${highlight ? 'text-blue-700 dark:text-blue-300' : ''}`}>{value}</div>
    </div>
  )
}
